import React, { useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import ReactApexChart from 'react-apexcharts'

import Rightbar from 'pages/main/cheques/Rightbar'
import { httpOk, GET } from 'helpers/api'
import { formatMoney, formatDate } from 'helpers/helpers'
import { StandardFields, FilterSegment } from 'pages/main/analytics/parts/AnalyticsFilterBar'
import useAnalyticsOptions from 'pages/main/analytics/parts/useAnalyticsOptions'
import { buildAnalyticsUrl } from 'pages/main/analytics/parts/analyticsDates'
import ChainFilterBar, { useChainFilters, ExcelButton } from '../parts/ChainFilterBar'
import { downloadExcel, periodText } from '../parts/chainExcel'
import {
	money, percent, columnStats, heatStyle, intensityStyle, buildRanks, chainColor,
	RankBadge, FillCell, useSort, SortTh, ChainKpi, HeatLegend, ChainEmpty,
} from '../parts/chainUi'

// Виды потерь: ключ суммы в строке филиала, доля от выручки, цвет
const KINDS = [
	{ id: 'discounts', amount: 'discount', percent: 'discount_percent', color: '#f59f00' },
	{ id: 'returns', amount: 'returns', percent: 'returns_percent', color: '#7c4dff' },
	{ id: 'deleted', amount: 'deleted', percent: 'deleted_percent', color: '#f06548' },
	{ id: 'items', amount: 'deleted_items', percent: 'deleted_items_percent', color: '#e83e8c' },
]

// Причины удаления: какая часть — удалённые чеки или удалённые позиции
const REASON_MODES = ['deleted', 'items']

function percent2(value) {
	return percent(value, Math.abs(Number(value || 0)) < 1 ? 2 : 1)
}

function Losses() {
	const { t } = useTranslation()
	const [data, setData] = useState(null)
	const [reasonMode, setReasonMode] = useState('deleted')
	const [details, setDetails] = useState(null)
	const [showRightBar, setShowRightBar] = useState(false)
	const [selectedCheque, setSelectedCheque] = useState({})

	const filters = useChainFilters({ order_type_ids: [], source_ids: [] }, searchWithFilter)
	const { applied } = filters
	const options = useAnalyticsOptions(['sources'])

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl('/chain/report/losses', filter), {}, { loader: false })
		if (httpOk(response)) setData(response.data)
	}

	const branches = useMemo(() => data?.branches || [], [data])
	const totals = data?.totals || {}
	const discountTypes = useMemo(() => data?.discount_types || [], [data])
	const reasons = useMemo(() => data?.reasons || [], [data])
	const branchById = useMemo(() => Object.fromEntries(branches.map(item => [item.pos_id, item])), [branches])

	const kindLabels = {
		discounts: t('discounts'),
		returns: t('chain_returns'),
		deleted: t('chain_deleted_cheques'),
		items: t('chain_deleted_items'),
	}
	const discountTypeLabels = {
		cashier_manual: t('chain_dt_cashier_manual'),
		auto_dish: t('chain_dt_auto_dish'),
		auto_cheque: t('chain_dt_auto_cheque'),
		manual_cheque: t('chain_dt_manual_cheque'),
		first_order: t('chain_dt_first_order'),
		promocode_generated: t('chain_dt_promocode_generated'),
		promocode_fixed: t('chain_dt_promocode_fixed'),
		manual_dish: t('chain_dt_manual_dish'),
		menu_promo: t('chain_dt_menu_promo'),
		buy_get: t('chain_dt_buy_get'),
		gift: t('chain_dt_gift'),
		other: t('chain_dt_other'),
	}
	const discountTypeName = (value) => discountTypeLabels[value] || value
	const reasonName = (reason) => reason.name || t('chain_no_reason')

	// Место: меньше потерь в % от выручки — выше
	const { ranks, count } = useMemo(() => buildRanks(branches.filter(item => item.revenue > 0), 'losses_percent', false), [branches])
	const { sorted, sort, toggle } = useSort(branches, 'losses_percent', 'asc')

	const stats = useMemo(() => ({
		discount_percent: columnStats(branches, 'discount_percent'),
		discount_cheques_percent: columnStats(branches, 'discount_cheques_percent'),
		returns_percent: columnStats(branches, 'returns_percent'),
		deleted_percent: columnStats(branches, 'deleted_percent'),
		deleted_items_percent: columnStats(branches, 'deleted_items_percent'),
		losses_percent: columnStats(branches, 'losses_percent'),
	}), [branches])
	const maxLossPercent = stats.losses_percent.max

	const withRevenue = branches.filter(item => item.losses_percent !== null && item.revenue > 0)
	const worst = withRevenue.length > 1 ? [...withRevenue].sort((a, b) => b.losses_percent - a.losses_percent)[0] : null

	async function openDetails(kind, posId, extra = {}, title = null) {
		const response = await GET(buildAnalyticsUrl('/chain/report/losses-details', applied, {
			kind,
			pos_id: posId || null,
			...extra,
		}), {}, { loader: true })
		if (httpOk(response)) {
			setDetails({
				...response.data,
				title: title || kindLabels[kind],
				pos_name: posId ? branchById[posId]?.pos_name : t('chain_all_branches'),
			})
		}
	}

	function closeDetails() {
		setDetails(null)
		setShowRightBar(false)
		setSelectedCheque({})
	}

	async function openCheque(chequeId) {
		if (!chequeId) return
		const response = await GET(`/cheques/${chequeId}`, {}, { loader: true })
		if (httpOk(response)) {
			setSelectedCheque(response.data.data)
			setShowRightBar(true)
		}
	}

	// Колонки расшифровки по виду операции: [заголовок, значение строки, тип для Excel]
	function detailColumns(kind) {
		const base = [
			{ label: t('date'), value: row => formatDate(row.date, 'dd.MM.yy HH:mm'), excel: row => formatDate(row.date, 'dd.MM.yyyy HH:mm'), type: 'text', width: 18 },
			{ label: t('chain_branch'), value: row => row.pos_name, type: 'text', width: 24 },
			{ label: t('chain_cheque_no'), value: row => row.cheque_number || '—', type: 'text', width: 10 },
		]
		if (kind === 'discounts') {
			return [
				...base,
				{ label: t('cashier'), value: row => row.cashier_name || '—', type: 'text', width: 18 },
				{ label: t('waiter'), value: row => row.waiter_name || '—', type: 'text', width: 18 },
				{ label: t('chain_discount_kind'), value: row => (row.discount_types || []).map(discountTypeName).join(', ') || discountTypeName('other'), type: 'text', width: 26 },
				{ label: t('chain_cheque_amount'), value: row => money(row.cheque_amount), excel: row => row.cheque_amount, type: 'money', num: true },
				{ label: t('discount'), value: row => money(row.amount), excel: row => row.amount, type: 'money', num: true, strong: true },
			]
		}
		if (kind === 'returns') {
			return [
				...base,
				{ label: t('cashier'), value: row => row.cashier_name || '—', type: 'text', width: 18 },
				{ label: t('comment'), value: row => row.comment || '—', type: 'text', width: 28 },
				{ label: t('chain_cheque_amount'), value: row => money(row.cheque_amount), excel: row => row.cheque_amount, type: 'money', num: true },
				{ label: t('amount'), value: row => money(row.amount), excel: row => row.amount, type: 'money', num: true, strong: true },
			]
		}
		if (kind === 'deleted') {
			return [
				...base,
				{ label: t('deleted_by'), value: row => row.deleted_by_name || '—', type: 'text', width: 18 },
				{ label: t('waiter'), value: row => row.waiter_name || '—', type: 'text', width: 18 },
				{ label: t('reason'), value: row => row.reason_name || t('chain_no_reason'), type: 'text', width: 22 },
				{ label: t('comment'), value: row => row.comment || '—', type: 'text', width: 24 },
				{ label: t('amount'), value: row => money(row.amount), excel: row => row.amount, type: 'money', num: true, strong: true },
			]
		}
		return [
			...base,
			{ label: t('chain_dish'), value: row => row.product_name || '—', type: 'text', width: 26 },
			{ label: t('quantity'), value: row => formatMoney(row.quantity, 3), excel: row => row.quantity, type: 'number', num: true, width: 9 },
			{ label: t('deleted_by'), value: row => row.deleted_by_name || '—', type: 'text', width: 18 },
			{ label: t('waiter'), value: row => row.waiter_name || '—', type: 'text', width: 18 },
			{ label: t('reason'), value: row => row.reason_name || t('chain_no_reason'), type: 'text', width: 22 },
			{ label: t('amount'), value: row => money(row.amount), excel: row => row.amount, type: 'money', num: true, strong: true },
		]
	}

	function exportExcel() {
		if (!data) return
		const period = `${t('period')}: ${periodText(data.start_date, data.end_date)}`
		const byLoss = buildRanks(branches.filter(item => item.revenue > 0), 'losses_percent', false).ranks
		const branchRow = (item, rank) => [
			rank, item.pos_name, item.revenue,
			item.discount, item.discount_percent, item.discount_cheques, item.discount_cheques_percent,
			item.returns, item.returns_percent, item.returns_count,
			item.deleted, item.deleted_percent, item.deleted_count,
			item.deleted_items, item.deleted_items_percent, item.deleted_items_lines,
			item.losses, item.losses_percent,
		]
		const sheets = [{
			name: t('chain_branches'),
			title: t('chain_losses_title'),
			subtitle: period,
			columns: [
				{ label: t('chain_rank'), type: 'int', width: 7 },
				{ label: t('chain_branch'), type: 'text', width: 28 },
				{ label: t('revenue'), type: 'money' },
				{ label: t('discounts'), type: 'money' },
				{ label: `${t('discounts')}, %`, type: 'percent', width: 10 },
				{ label: t('chain_discount_cheques'), type: 'int', width: 11 },
				{ label: `${t('chain_discount_cheques')}, %`, type: 'percent', width: 11 },
				{ label: t('chain_returns'), type: 'money' },
				{ label: `${t('chain_returns')}, %`, type: 'percent', width: 10 },
				{ label: t('chain_returns_count'), type: 'int', width: 10 },
				{ label: t('chain_deleted_cheques'), type: 'money' },
				{ label: `${t('chain_deleted_cheques')}, %`, type: 'percent', width: 10 },
				{ label: t('chain_deleted_count'), type: 'int', width: 10 },
				{ label: t('chain_deleted_items'), type: 'money' },
				{ label: `${t('chain_deleted_items')}, %`, type: 'percent', width: 10 },
				{ label: t('chain_deleted_lines'), type: 'int', width: 10 },
				{ label: t('chain_total_losses'), type: 'money' },
				{ label: t('chain_percent_of_revenue'), type: 'percent', width: 11 },
			],
			rows: [...branches].sort((a, b) => Number(a.losses_percent ?? 1e9) - Number(b.losses_percent ?? 1e9)).map(item => branchRow(item, byLoss[item.pos_id] || '')),
			footer: branchRow({ ...totals, pos_name: t('total') }, ''),
		}]
		if (discountTypes.length > 0) {
			sheets.push({
				name: t('chain_discount_kinds'),
				title: `${t('chain_losses_title')} — ${t('chain_discount_kinds')}`,
				subtitle: period,
				columns: [
					{ label: t('chain_discount_kind'), type: 'text', width: 30 },
					...branches.map(branch => ({ label: branch.pos_name, type: 'money' })),
					{ label: t('total'), type: 'money' },
					{ label: t('share'), type: 'percent', width: 9 },
				],
				rows: discountTypes.map(type => [
					discountTypeName(type.value),
					...branches.map(branch => Number(type.poses?.[branch.pos_id] || 0)),
					type.amount, type.share,
				]),
				footer: [t('total'), ...branches.map(branch => branch.discount), totals.discount, 100],
			})
		}
		if (reasons.length > 0) {
			sheets.push({
				name: t('deletion_reasons'),
				title: `${t('chain_losses_title')} — ${t('deletion_reasons')}`,
				subtitle: period,
				columns: [
					{ label: t('reason'), type: 'text', width: 30 },
					...branches.flatMap(branch => [
						{ label: `${branch.pos_name}: ${t('chain_deleted_cheques')}`, type: 'money' },
						{ label: `${branch.pos_name}: ${t('chain_deleted_items')}`, type: 'money' },
					]),
					{ label: t('chain_deleted_cheques'), type: 'money' },
					{ label: t('chain_deleted_count'), type: 'int', width: 10 },
					{ label: t('chain_deleted_items'), type: 'money' },
					{ label: t('chain_deleted_lines'), type: 'int', width: 10 },
					{ label: t('total'), type: 'money' },
					{ label: t('share'), type: 'percent', width: 9 },
				],
				rows: reasons.map(reason => [
					reasonName(reason),
					...branches.flatMap(branch => [
						Number(reason.deleted_poses?.[branch.pos_id] || 0),
						Number(reason.items_poses?.[branch.pos_id] || 0),
					]),
					reason.deleted, reason.deleted_count, reason.deleted_items, reason.deleted_items_lines, reason.amount, reason.share,
				]),
			})
		}
		downloadExcel(`${t('chain_losses_title')} ${periodText(data.start_date, data.end_date)}`, sheets)
	}

	function exportDetails() {
		if (!details) return
		const columns = detailColumns(details.kind)
		downloadExcel(`${details.title} — ${details.pos_name}`, [{
			name: details.title,
			title: `${details.title} — ${details.pos_name}`,
			subtitle: `${t('period')}: ${periodText(applied.start_date, applied.end_date)}`,
			columns: columns.map(column => ({ label: column.label, type: column.type, width: column.width })),
			rows: details.rows.map(row => columns.map(column => {
				const value = (column.excel || column.value)(row)
				return value === '—' ? '' : value
			})),
			footer: columns.map((column, index) => index === 0 ? t('total') : (index === columns.length - 1 ? details.total : '')),
		}])
	}

	const reasonModeLabels = { deleted: t('chain_deleted_cheques'), items: t('chain_deleted_items') }
	const reasonPoses = (reason) => reason[reasonMode === 'deleted' ? 'deleted_poses' : 'items_poses'] || {}
	const reasonCounts = (reason) => reason[reasonMode === 'deleted' ? 'deleted_counts' : 'items_counts'] || {}
	const reasonTotal = (reason) => reasonMode === 'deleted' ? reason.deleted : reason.deleted_items
	const reasonsShown = reasons.filter(reason => reasonTotal(reason) > 0)
	const reasonModeTotal = reasonMode === 'deleted' ? totals.deleted : totals.deleted_items

	// Ячейка вида потерь в рейтинге: сумма, доля от выручки, количество
	function renderKindCell(branch, kind, countKey, countLabel) {
		const amount = Number(branch[kind.amount] || 0)
		return (
			<td className={`chain-num ${amount > 0 ? 'chain-click' : ''}`}
				style={heatStyle(branch[kind.percent], stats[kind.percent], false)}
				onClick={() => amount > 0 && openDetails(kind.id, branch.pos_id)}>
				<span>{amount > 0 ? money(amount) : '—'}</span>
				{amount > 0 && (
					<div className="chain-muted">{percent2(branch[kind.percent])} · {formatMoney(branch[countKey] || 0)} {countLabel}</div>
				)}
			</td>
		)
	}

	return (
		<>
			<ChainFilterBar
				title={t('chain_losses_title')}
				icon="uil-receipt-alt"
				filters={filters}
				onExcel={exportExcel}
				excelDisabled={!data}
			>
				<StandardFields filters={filters} options={options} branch={false} />
			</ChainFilterBar>

			<div className="row g-3 mb-3">
				<div className="col-xl col-md-4 col-6">
					<ChainKpi title={t('chain_total_losses')} icon="uil-chart-down" color="#f06548"
						value={money(totals.losses)}
						sub={<>{percent2(totals.losses_percent)} {t('chain_of_revenue')}</>} />
				</div>
				{KINDS.map(kind => {
					const countKey = { discounts: 'discount_cheques', returns: 'returns_count', deleted: 'deleted_count', items: 'deleted_items_lines' }[kind.id]
					const countLabel = kind.id === 'items' ? t('chain_lines_lower') : t('chain_cheques_lower')
					return (
						<div className="col-xl col-md-4 col-6" key={kind.id}>
							<ChainKpi title={kindLabels[kind.id]} icon={{ discounts: 'uil-percentage', returns: 'uil-redo', deleted: 'uil-trash-alt', items: 'uil-minus-circle' }[kind.id]} color={kind.color}
								value={money(totals[kind.amount])}
								sub={<>{percent2(totals[kind.percent])} · {formatMoney(totals[countKey] || 0)} {countLabel}</>} />
						</div>
					)
				})}
				<div className="col-xl col-md-4 col-6">
					<ChainKpi title={t('chain_loses_most')} icon="uil-exclamation-triangle" color="#f06548"
						value={worst ? worst.pos_name : '—'}
						sub={worst ? <>{percent2(worst.losses_percent)} {t('chain_of_revenue')} · {money(worst.losses)}</> : null} />
				</div>
			</div>

			<div className="card chain-card mb-3">
				<div className="card-body">
					<div className="chain-toolbar">
						<div className="chain-toolbar-group">
							<h5 className="mb-0 me-2">{t('chain_losses_rating')}</h5>
							<span className="chain-muted">{t('chain_losses_rank_hint')}</span>
						</div>
						<HeatLegend />
					</div>

					<div className="chain-scroll">
						<table className="chain-table">
							<thead>
								<tr>
									<th className="chain-index chain-sticky">#</th>
									<SortTh sortKey="pos_name" sort={sort} onSort={toggle} className="chain-sticky-2">{t('chain_branch')}</SortTh>
									<SortTh sortKey="revenue" sort={sort} onSort={toggle} className="text-end">{t('revenue')}</SortTh>
									<SortTh sortKey="discount" sort={sort} onSort={toggle} className="text-end">{t('discounts')}</SortTh>
									<SortTh sortKey="discount_cheques_percent" sort={sort} onSort={toggle} className="text-end" title={t('chain_discount_cheques_hint')}>{t('chain_discount_cheques')}</SortTh>
									<SortTh sortKey="returns" sort={sort} onSort={toggle} className="text-end">{t('chain_returns')}</SortTh>
									<SortTh sortKey="deleted" sort={sort} onSort={toggle} className="text-end" title={t('chain_deleted_cheques_hint')}>{t('chain_deleted_cheques')}</SortTh>
									<SortTh sortKey="deleted_items" sort={sort} onSort={toggle} className="text-end" title={t('chain_deleted_items_hint')}>{t('chain_deleted_items')}</SortTh>
									<SortTh sortKey="losses" sort={sort} onSort={toggle} className="text-end chain-total-col">{t('chain_total_losses')}</SortTh>
									<SortTh sortKey="losses_percent" sort={sort} onSort={toggle} className="text-end" title={t('chain_percent_of_revenue_hint_losses')}>{t('chain_percent_of_revenue')}</SortTh>
								</tr>
							</thead>
							<tbody>
								{sorted.length === 0 && <ChainEmpty colSpan={10} />}
								{sorted.map(item => {
									const index = branches.indexOf(item)
									return (
										<tr key={item.pos_id}>
											<td className="chain-index chain-sticky"><RankBadge rank={ranks[item.pos_id]} count={count} /></td>
											<td className="chain-name chain-sticky-2">
												<span className="d-inline-flex align-items-center">
													<span className="chain-dot" style={{ background: chainColor(index) }}></span>
													{item.pos_name}
												</span>
											</td>
											<td className="chain-num">
												{money(item.revenue)}
												<div className="chain-muted">{formatMoney(item.cheques)} {t('chain_cheques_lower')}</div>
											</td>
											{renderKindCell(item, KINDS[0], 'discount_cheques', t('chain_cheques_lower'))}
											<td className="chain-num" style={heatStyle(item.discount_cheques_percent, stats.discount_cheques_percent, false)}>
												{formatMoney(item.discount_cheques)}
												<div className="chain-muted">{percent(item.discount_cheques_percent)}</div>
											</td>
											{renderKindCell(item, KINDS[1], 'returns_count', t('chain_pcs'))}
											{renderKindCell(item, KINDS[2], 'deleted_count', t('chain_pcs'))}
											{renderKindCell(item, KINDS[3], 'deleted_items_lines', t('chain_lines_lower'))}
											<td className="chain-num chain-total-col">{money(item.losses)}</td>
											<FillCell percent={maxLossPercent > 0 ? Number(item.losses_percent || 0) / maxLossPercent * 100 : 0} tone="danger"
												style={heatStyle(item.losses_percent, stats.losses_percent, false)}>
												<b>{percent2(item.losses_percent)}</b>
											</FillCell>
										</tr>
									)
								})}
							</tbody>
							{sorted.length > 0 && (
								<tfoot>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2">{t('total')}</td>
										<td className="chain-num">{money(totals.revenue)}</td>
										<td className="chain-num chain-click" onClick={() => totals.discount > 0 && openDetails('discounts', null)}>{money(totals.discount)}<div className="chain-muted">{percent2(totals.discount_percent)}</div></td>
										<td className="chain-num">{formatMoney(totals.discount_cheques)}<div className="chain-muted">{percent(totals.discount_cheques_percent)}</div></td>
										<td className="chain-num chain-click" onClick={() => totals.returns > 0 && openDetails('returns', null)}>{money(totals.returns)}<div className="chain-muted">{percent2(totals.returns_percent)}</div></td>
										<td className="chain-num chain-click" onClick={() => totals.deleted > 0 && openDetails('deleted', null)}>{money(totals.deleted)}<div className="chain-muted">{percent2(totals.deleted_percent)}</div></td>
										<td className="chain-num chain-click" onClick={() => totals.deleted_items > 0 && openDetails('items', null)}>{money(totals.deleted_items)}<div className="chain-muted">{percent2(totals.deleted_items_percent)}</div></td>
										<td className="chain-num chain-total-col">{money(totals.losses)}</td>
										<td className="chain-num">{percent2(totals.losses_percent)}</td>
									</tr>
								</tfoot>
							)}
						</table>
					</div>
					<div className="chain-muted mt-2">
						<i className="uil uil-info-circle me-1"></i>
						{t('chain_losses_hint')}
					</div>
				</div>
			</div>

			<div className="row g-3 mb-3">
				<div className="col-xl-6">
					<div className="card chain-card mb-0 h-100">
						<div className="card-body">
							<div className="chain-toolbar">
								<h5 className="mb-0">{t('chain_discount_kinds')}</h5>
								<span className="chain-muted">{money(totals.discount)}</span>
							</div>
							<div className="chain-scroll chain-scroll-auto">
								<table className="chain-table">
									<thead>
										<tr>
											<th>{t('chain_discount_kind')}</th>
											{branches.map(branch => <th key={branch.pos_id} className="text-end">{branch.pos_name}</th>)}
											<th className="text-end chain-total-col">{t('total')}</th>
											<th className="text-end">{t('share')}</th>
										</tr>
									</thead>
									<tbody>
										{discountTypes.length === 0 && <ChainEmpty colSpan={branches.length + 3} text={t('chain_no_discounts')} />}
										{discountTypes.map(type => {
											const values = branches.map(branch => Number(type.poses?.[branch.pos_id] || 0))
											const rowMax = Math.max(0, ...values)
											const title = `${t('discounts')}: ${discountTypeName(type.value)}`
											return (
												<tr key={type.type_id}>
													<td className="chain-name chain-click" onClick={() => openDetails('discounts', null, { discount_type: type.type_id }, title)}>
														{discountTypeName(type.value)}
													</td>
													{branches.map((branch, index) => (
														<td key={branch.pos_id} className={`chain-num ${values[index] > 0 ? 'chain-click' : ''}`}
															style={intensityStyle(values[index], rowMax, 'danger')}
															onClick={() => values[index] > 0 && openDetails('discounts', branch.pos_id, { discount_type: type.type_id }, title)}>
															{values[index] > 0 ? money(values[index]) : '—'}
														</td>
													))}
													<td className="chain-num chain-total-col">{money(type.amount)}</td>
													<FillCell percent={type.share} tone="warning">{percent(type.share)}</FillCell>
												</tr>
											)
										})}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
				<div className="col-xl-6">
					<div className="card chain-card mb-0 h-100">
						<div className="card-body">
							<div className="chain-toolbar">
								<h5 className="mb-0">{t('deletion_reasons')}</h5>
								<FilterSegment
									options={REASON_MODES.map(id => ({ id, label: reasonModeLabels[id] }))}
									value={reasonMode}
									onChange={setReasonMode}
								/>
							</div>
							<div className="chain-scroll chain-scroll-auto">
								<table className="chain-table">
									<thead>
										<tr>
											<th>{t('reason')}</th>
											{branches.map(branch => <th key={branch.pos_id} className="text-end">{branch.pos_name}</th>)}
											<th className="text-end chain-total-col">{t('total')}</th>
											<th className="text-end">{t('share')}</th>
										</tr>
									</thead>
									<tbody>
										{reasonsShown.length === 0 && <ChainEmpty colSpan={branches.length + 3} text={t('chain_no_deletions')} />}
										{reasonsShown.map(reason => {
											const poses = reasonPoses(reason)
											const counts = reasonCounts(reason)
											const values = branches.map(branch => Number(poses[branch.pos_id] || 0))
											const rowMax = Math.max(0, ...values)
											const total = reasonTotal(reason)
											const share = reasonModeTotal > 0 ? total / reasonModeTotal * 100 : 0
											const title = `${reasonModeLabels[reasonMode]}: ${reasonName(reason)}`
											return (
												<tr key={reason.reason_id}>
													<td className="chain-name chain-click" onClick={() => openDetails(reasonMode, null, { reason_id: reason.reason_id }, title)}>
														{reasonName(reason)}
													</td>
													{branches.map((branch, index) => (
														<td key={branch.pos_id} className={`chain-num ${values[index] > 0 ? 'chain-click' : ''}`}
															style={intensityStyle(values[index], rowMax, 'danger')}
															title={values[index] > 0 ? `${formatMoney(counts[branch.pos_id] || 0)} ${reasonMode === 'items' ? t('chain_lines_lower') : t('chain_cheques_lower')}` : undefined}
															onClick={() => values[index] > 0 && openDetails(reasonMode, branch.pos_id, { reason_id: reason.reason_id }, title)}>
															{values[index] > 0 ? money(values[index]) : '—'}
														</td>
													))}
													<td className="chain-num chain-total-col">{money(total)}</td>
													<FillCell percent={share} tone="danger">{percent(share)}</FillCell>
												</tr>
											)
										})}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>

			<StructureChart branches={branches} kindLabels={kindLabels} />

			{/* MODAL DETAILS */}
			<Modal show={!!details} onHide={closeDetails} size="xl" centered scrollable enforceFocus={false}>
				<Modal.Header closeButton>
					<Modal.Title>
						{details?.title} — {details?.pos_name}
						<div className="chain-muted fw-normal">{periodText(applied.start_date, applied.end_date)} · {formatMoney(details?.rows?.length || 0)} {t('chain_operations_lower')} · {money(details?.total)}</div>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ height: '520px' }}>
					{details?.limited && (
						<div className="chain-note mb-2"><i className="uil uil-exclamation-triangle"></i>{t('chain_details_limited')}</div>
					)}
					{details && (
						<table className="chain-table">
							<thead>
								<tr>
									<th>#</th>
									{detailColumns(details.kind).map(column => (
										<th key={column.label} className={column.num ? 'text-end' : undefined}>{column.label}</th>
									))}
								</tr>
							</thead>
							<tbody>
								{details.rows.length === 0 && <ChainEmpty colSpan={detailColumns(details.kind).length + 1} />}
								{details.rows.map((row, index) => (
									<tr key={`${row.id}-${index}`} className={`chain-click ${showRightBar && selectedCheque.id === row.cheque_id ? 'table-tr-active' : ''}`}
										onClick={() => openCheque(row.cheque_id)} title={t('chain_open_cheque')}>
										<td className="chain-index">{index + 1}</td>
										{detailColumns(details.kind).map(column => (
											<td key={column.label} className={column.num ? 'chain-num' : (column.type === 'text' && column.width >= 22 ? '' : 'text-nowrap')}
												style={column.width >= 24 ? { maxWidth: '260px' } : undefined}>
												{column.strong ? <b>{column.value(row)}</b> : column.value(row)}
											</td>
										))}
									</tr>
								))}
							</tbody>
						</table>
					)}
				</Modal.Body>
				<Modal.Footer>
					<ExcelButton onClick={exportDetails} disabled={!details?.rows?.length} />
					<button type="button" className="btn btn-outline-secondary" onClick={closeDetails}>{t('close')}</button>
				</Modal.Footer>
			</Modal>

			<Rightbar
				showRightBar={showRightBar}
				setShowRightBar={setShowRightBar}
				selectedItem={selectedCheque}>
			</Rightbar>
		</>
	)
}

// Из чего складываются потери филиала — в % от его выручки
function StructureChart({ branches, kindLabels }) {
	const { t } = useTranslation()
	const rows = [...branches].filter(item => item.revenue > 0).sort((a, b) => Number(b.losses_percent || 0) - Number(a.losses_percent || 0))
	if (rows.length === 0) return null

	const series = KINDS.map(kind => ({
		name: kindLabels[kind.id],
		data: rows.map(branch => Number(branch[kind.percent] || 0)),
	}))
	const options = {
		chart: { type: 'bar', stacked: true, toolbar: { show: false } },
		plotOptions: { bar: { horizontal: true, borderRadius: 2, barHeight: '62%' } },
		colors: KINDS.map(kind => kind.color),
		dataLabels: { enabled: false },
		legend: { position: 'bottom', fontSize: '12px' },
		grid: { borderColor: '#eef2f6', strokeDashArray: 3 },
		xaxis: { categories: rows.map(branch => branch.pos_name), labels: { formatter: value => `${Number(value).toFixed(1)}%` } },
		tooltip: { y: { formatter: value => `${Number(value).toFixed(2)}%` } },
	}

	return (
		<div className="card chain-card mb-3">
			<div className="card-body">
				<div className="chain-card-title"><h5>{t('chain_losses_structure')}</h5></div>
				<ReactApexChart type="bar" height={Math.max(300, rows.length * 40 + 90)} series={series} options={options} />
			</div>
		</div>
	)
}

export default Losses
